import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbCheck, TbCopy } from "solid-icons/tb";
import { type Component, Show } from "solid-js";
import { twMerge } from "tailwind-merge";
import { useCopy } from "./useCopy";

export interface CopyButtonProps {
  text: string;
  position?: "absolute" | "inline";
  showTooltip?: boolean;
  animateOnHover?: boolean;
  class?: string;
  onClick?: (e: MouseEvent) => void;
}

/**
 * Shared copy button component with consistent styling and behavior
 */
export const CopyButton: Component<CopyButtonProps> = (props) => {
  const { copied, copy } = useCopy({ timeout: 1500 });

  const handleCopy = async (e: MouseEvent) => {
    e.stopPropagation();
    if (props.onClick) {
      props.onClick(e);
    }
    await copy(props.text);
  };

  const buttonClass = twMerge(
    "p-1.5 rounded-lg bg-tile hover:bg-tile-hover active:bg-tile-hover",
    "border border-primary-border shadow-sm",
    "transition-all duration-200",
    "focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1",
    props.position === "absolute" && "absolute top-2 right-2",
    props.animateOnHover && "opacity-0 group-hover:opacity-100",
    props.class,
  );

  const button = (
    <button
      type="button"
      onClick={handleCopy}
      class={buttonClass}
      title={copied() ? "Copied!" : "Copy to clipboard"}
      aria-label={copied() ? "Copied!" : "Copy to clipboard"}
    >
      <Show
        when={copied()}
        fallback={<TbCopy class="w-4 h-4 text-secondary-text hover:text-primary-text transition-colors" />}
      >
        <TbCheck class="w-4 h-4 text-white" />
      </Show>
    </button>
  );

  if (!props.showTooltip) {
    return button;
  }

  return (
    <StTooltip placement="top" content={<div>{copied() ? "Copied!" : "Copy to clipboard"}</div>}>
      <StTooltipTrigger as="button" onClick={handleCopy} class={buttonClass}>
        <Show
          when={copied()}
          fallback={<TbCopy class="w-4 h-4 text-secondary-text hover:text-primary-text transition-colors" />}
        >
          <TbCheck class="w-4 h-4 text-white" />
        </Show>
      </StTooltipTrigger>
    </StTooltip>
  );
};
